import React, { memo, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';

import AddFlatNodeForm from '@components/common/FlatTree/FlatTreeNodeWrapper/AddFlatNodeForm';
import EditFlatNodeForm from '@components/common/FlatTree/FlatTreeNodeWrapper/EditFlatNodeForm';
import FlatTreeNodeError from '@components/common/FlatTree/FlatTreeNodeWrapper/FlatTreeNodeError';

import { EValidationRuleType } from '@utils/treeValidation';

import { IBasicNode } from '@interfaces/components/tree';
import { IValidationRuleDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { IFlatTreeValidationRule, TFlatTreeGetValidation } from '@components/common/FlatTree/interface';

import { ETreeTypeNode } from '@enums/tree';

import {
	LINE_NUMBER_OFFSET,
	TAG_OFFSET,
	XML_NODE_PADDING,
	XML_NODE_VIRTUALIZED_DEFAULT_HEIGHT,
} from '../constants';

import { IAddXMLNode, IFlatXMLTreeError, IFlatXMLTreeNode } from '../interface';

import FlatTreeNode from './FlatTreeXMLNode';

import styles from './FlatTreeXMLNodeWrapper.module.scss';

interface IFlatTreeNodeWrapperProps {
	node: IFlatXMLTreeNode;
	index: string;
	onSkip: (id: string) => void;
	onCollapse: (id: string, path: string) => void;
	onEdit: (id: string, value: string) => void;
	onAdd: <T extends IBasicNode = any>(data: IAddXMLNode<T>) => void;
	onRemove: (id: string) => void;
	onRevert: (id: string) => void;
	onCopy: (id: string) => void;
	getValidationUpdateString?: (id: string) => Promise<IValidationRuleDTO | undefined>;
	getValidationRules?: TFlatTreeGetValidation;
	getChildrenNamesList?: <T extends IBasicNode = any>(node: T) => string[];
	isCollapsed?: boolean;
	isReadOnly?: boolean;
	isWithSkip?: boolean;
	isWithCopy?: boolean;
	isWithAdd?: boolean;
	isWithToggle?: boolean;
	isWithLineNumbers?: boolean;
	style?: React.CSSProperties;
	searchValue?: string;
	errors?: Map<string, IFlatXMLTreeError>;
}

const FlatTreeNodeWrapper = ({
	node,
	onSkip,
	onCollapse,
	onAdd,
	onEdit,
	onCopy,
	onRemove,
	onRevert,
	getValidationRules,
	getValidationUpdateString,
	getChildrenNamesList,
	isReadOnly,
	isWithSkip,
	isWithCopy,
	isWithToggle,
	isWithLineNumbers = true,
	isWithAdd,
	index,
	style,
	searchValue,
	errors,
}: IFlatTreeNodeWrapperProps) => {
	const [isEditMode, setIsEditMode] = useState(false);
	const [isAddMode, setIsAddMode] = useState(false);
	const [isError, setIsError] = useState(false);
	const [nodeValidation, setNodeValidation] = useState<any>();

	const offsetWithoutLineNumbers = 0;
	const offsetWithLineNumbers =
		node.depth * XML_NODE_PADDING + LINE_NUMBER_OFFSET + (!node.nodeType ? TAG_OFFSET : 0);

	const treeNodeOffset = isWithLineNumbers ? offsetWithLineNumbers : offsetWithoutLineNumbers;

	const toggleCollapse = useCallback(() => onCollapse(index, node.path), [node.path, index, onCollapse]);

	const callValidation = useCallback(async (callback: (rules?: IFlatTreeValidationRule[]) => void) => {
		if (getValidationRules && getValidationUpdateString) {
			try {
				const params = await getValidationUpdateString(node.id);

				if (!params) {
					return;
				}

				const { rules, validations } = await getValidationRules(params!);

				setNodeValidation(validations);
				callback(rules);
			} catch {
				setNodeValidation(undefined);
			}
		} else if (!getValidationRules) {
			callback();
		}
	}, [
		getValidationRules,
		getValidationUpdateString,
		node.id,
	]);

	const removeNodeHandler = useCallback(() => {
		void callValidation((rules?: IFlatTreeValidationRule[]) => {
			const isMandatory = rules?.some((rule) => {
				return rule.type.name === EValidationRuleType.MANDATORY && rule.isMandatory;
			});

			onRemove(node?.id);

			if (!isMandatory) {
				setIsError(false);

				return;
			}

			setIsError(true);
		});
	}, [node, onRemove, callValidation]);

	const copyNodeHandler = useCallback(() => onCopy(node.id), [node, onCopy]);

	const enterAddMode = useCallback(() => {
		void callValidation(() => setIsAddMode(true));
	}, [callValidation]);

	const enterEditMode = useCallback(() => {
		void callValidation(() => setIsEditMode(true));
	}, [callValidation]);

	const handlerRevert = useCallback(() => {
		onRevert(index);
	}, [index, onRevert]);

	const skipNodeHandler = useCallback(() => onSkip(index), [index, onSkip]);

	const usedKeys = useMemo(() => {
		return getChildrenNamesList ? getChildrenNamesList(node) : [];
	}, [getChildrenNamesList, node]);

	const availableValues = useMemo(() => {
		return getChildrenNamesList && nodeValidation?.availableValues
			? nodeValidation?.availableValues.filter((item: string) => !usedKeys.includes(item))
			: [];
	}, [getChildrenNamesList, nodeValidation, usedKeys]);

	const parentNodeValidation = useMemo(() => {
		return nodeValidation ?
			{
				...nodeValidation,
				availableValues,
			} : {};
	}, [availableValues, nodeValidation]);

	const isNoKeysAvailable = usedKeys.length > 0
		&& nodeValidation?.availableValues?.length !== 0
		&& availableValues.length === 0;

	const exitEditMode = useCallback(() => setIsEditMode(false), []);
	const exitAddMode = useCallback(() => setIsAddMode(false), []);

	const error = errors?.get(node.keysPath);

	return (
		<>
			<div style={{ ...style }}>
				<div
					className={styles.nodeWrapper}
					style={{ paddingLeft: treeNodeOffset }}
				>
					{node.lineNumber && isWithLineNumbers && (
						<span className={styles.lineNumber}>{node.lineNumber}</span>
					)}
					{!isEditMode && (
						<FlatTreeNode
							enterAddMode={node.nodeType && isWithAdd ? enterAddMode : undefined}
							enterEditMode={enterEditMode}
							error={error}
							isCollapsed={node.isHiddenChildren}
							isReadOnly={isReadOnly}
							isWithAdd={isWithAdd}
							isWithCopy={isWithCopy}
							isWithLineNumbers={isWithLineNumbers}
							isWithSkip={isWithSkip}
							isWithToggle={isWithToggle}
							node={node}
							onCopy={copyNodeHandler}
							onRemove={removeNodeHandler}
							onRevert={handlerRevert}
							searchValue={searchValue}
							skipNodeHandler={skipNodeHandler}
							toggleCollapse={toggleCollapse}
						/>
					)}
					{isAddMode && (
						<AddFlatNodeForm
							availableValues={nodeValidation?.availableValues}
							exitAddMode={exitAddMode}
							getChildrenNamesList={getChildrenNamesList}
							getValidationRules={getValidationRules}
							isArray={node.nodeType === ETreeTypeNode.array}
							isNoKeysAvailable={isNoKeysAvailable}
							node={node}
							onSubmit={onAdd}
							parentNodeValidation={parentNodeValidation}
						/>
					)}
					{isEditMode && (
						<EditFlatNodeForm
							className={styles.editWrapper}
							exitEditMode={exitEditMode}
							node={node}
							nodeValidation={nodeValidation}
							onSubmit={onEdit}
						/>
					)}
					{isError && (
						<FlatTreeNodeError
							errorText='Field is mandatory'
						/>
					)}
				</div>
			</div>
			{node.tags.map((item, index) => {
				const key = `closing_tag_${node.id}_${item.depth}_${index}`;

				const paddingLeft = isWithLineNumbers ? item.depth * XML_NODE_PADDING + LINE_NUMBER_OFFSET : 0;
				const lineNumber = (item.lineNumber || 0) + 1;

				const wrapperTopStyle = { top: `calc(${style?.top}px + ${XML_NODE_VIRTUALIZED_DEFAULT_HEIGHT}px)` };
				const tagTopStyle = { top: `${XML_NODE_VIRTUALIZED_DEFAULT_HEIGHT * index}px` };
				const closingTag = '</' + item.value + '>';

				return (
					<div
						key={key}
						style={{ ...style, ...wrapperTopStyle }}
					>
						{isWithLineNumbers && (
							<span
								className={styles.lineNumber}
								style={tagTopStyle}
							>
								{lineNumber}
							</span>
						)}
						<div
							className={cn(styles.closingTag, { [styles.nodeChanged]: item.isAdded })}
							style={{
								paddingLeft,
								...tagTopStyle,
								marginLeft: isWithLineNumbers ? 0 : treeNodeOffset,
								left: isWithLineNumbers ? 0 : style?.left,
							}}
						>
							{closingTag}
						</div>
					</div>
				);
			})}
		</>
	);
};

export default memo(FlatTreeNodeWrapper);
